import { useState } from 'react';
import { Button, ButtonVariants } from '@components/button/Button';
import { Modal } from '@components/modal/Modal';
import { ModalActions } from '@components/modal/ModalActions';
import { errorToast, successToast } from '@components/toasts/Toasts';
import { useAbortEffect } from '@shared/hooks/useAbortEffect';
import { reportAppError } from '@shared/reportAppError';
import { getUpcomingReservationsByListing } from 'restaurantAdmin/reservations/apiHelpers';
import type { ListingWithShifts } from '../apiHelpers';
import { deleteListing } from '../apiHelpers';
import { useListingsContext } from '../ListingsContext';

export interface DeleteListingConfirmationModalProps {
  closeModal: () => void;
  isOpen: boolean;
  listing: ListingWithShifts;
}

export const DeleteListingConfirmationModal = ({
  closeModal,
  isOpen,
  listing,
}: DeleteListingConfirmationModalProps) => {
  const [numOfUpcomingReservations, setNumOfUpcomingReservations] = useState(0);

  const { refreshListings, clearSelectedListing } = useListingsContext();

  useAbortEffect(
    async (signal) => {
      if (isOpen) {
        const response = await getUpcomingReservationsByListing(
          listing.restaurantId,
          listing.id,
          signal,
        );
        setNumOfUpcomingReservations(response.length);
      }
    },
    [isOpen],
  );

  const noUpcomingReservationMessage = `${listing.name} will be permanently deleted.`;
  const oneUpcomingReservationMessage = `Deleting ${listing.name} will affect 1 upcoming reservation. That reservation must be reconciled in Unassigned Reservations.`;
  const multipleUpcomingReservationsMessage = `Deleting ${listing.name} will affect ${numOfUpcomingReservations} upcoming reservations. Those reservations must be reconciled in Unassigned Reservations.`;

  let message;
  if (numOfUpcomingReservations === 0) {
    message = noUpcomingReservationMessage;
  } else if (numOfUpcomingReservations === 1) {
    message = oneUpcomingReservationMessage;
  } else {
    message = multipleUpcomingReservationsMessage;
  }

  const onConfirmDeleteListing = () => {
    void (async () => {
      try {
        await deleteListing(listing.restaurantId, listing.id);
        successToast({ message: 'Listing successfully deleted' });
        refreshListings();
        clearSelectedListing();
      } catch (e) {
        errorToast({ message: 'Failed to delete the listing' });
        reportAppError(e);
      }
    })();
    closeModal();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeModal}
      title="Delete Listing"
      subtitle={message}
    >
      <ModalActions>
        <Button
          label="Keep Listing"
          onClick={closeModal}
          variant={ButtonVariants.Tertiary}
        />
        <Button
          label="Delete Listing"
          onClick={onConfirmDeleteListing}
          variant={ButtonVariants.Error}
        />
      </ModalActions>
    </Modal>
  );
};
